/**
 * FixAllSheet — Accounts S7 (spec §3.2 Clean-ups tile "Fix all ›"; ruling of
 * 2026-10-27): the preview of every suggested fix, from
 * GET /v1/sales/accounts/cleanups. Nothing changes until the user acts.
 *
 *   - Tidy names: every lower-case name with what it becomes, all selected;
 *     "Tidy n names" applies them as ONE batch (POST /v1/sales/accounts/
 *     tidies) — one toast, one Undo, backed by the field-history rows the
 *     batch answers with.
 *   - Possible duplicates: each pair with what a merge would do (the merge
 *     preview's counts and survivor). A high-confidence pair starts selected,
 *     a medium one never does. "Review n merges" hands the selection to the
 *     page, which opens S6's merge dialog for each — its own confirm, its own
 *     toast with Undo. Merging is owner/admin only: without canMerge the
 *     pairs are listed with no merge control and no instruction.
 *
 * Since §44 (ruling of 2026-10-30) the sheet opens for owner/admin only (the
 * page gates "Fix all ›" on writeAccess.records), so canMerge holds whenever
 * it shows; "Tidy n names" is gated on records here as well (fail closed).
 *
 * Visual: the house .k-drawer (AccountWebsitesDrawer's shape), k-btn family,
 * no new tokens.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'
import { fetchAccountCleanups, tidyAccountNames, undoAccountNameTidies } from '../../api'
import type { AccountMergeSuggestion, SalesAccountCleanups } from '../../../../api/types'
import { apiErrorMessage } from '../../../../api/client'
import { overlay } from '../../../../theme/tokens'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { plural } from './accountMergeActions'
import { defaultSelection, mergeKey, mergeLine, mergeReason, mergeTitle, tidiedToast, tidyUndoneToast } from './accountCleanups'

export function FixAllSheet({ canMerge, onClose, onChanged, onReviewMerges }: {
  /** S6 ruling 1: owner or admin per /v1/me. */
  canMerge: boolean
  onClose: () => void
  /** A write landed (the tidy or its Undo): the page re-reads. */
  onChanged: () => void
  /** The selected merges, in list order, each to be confirmed on its own. */
  onReviewMerges: (merges: AccountMergeSuggestion[]) => void
}) {
  const [data, setData] = useState<SalesAccountCleanups | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tidies, setTidies] = useState<Set<string>>(new Set())
  const [merges, setMerges] = useState<Set<string>>(new Set())
  const [nonce, setNonce] = useState(0)
  const [tidying, setTidying] = useState(false)
  // The tidy batch is an account edit: records-level (lib/writeAccess).
  const { records } = useWriteAccess()

  useEffect(() => {
    let alive = true
    fetchAccountCleanups()
      .then(c => {
        if (!alive) return
        const sel = defaultSelection(c)
        setData(c)
        setTidies(sel.tidies)
        setMerges(sel.merges)
      })
      .catch(e => { if (alive) setError(apiErrorMessage(e, 'Failed to read the clean-ups')) })
    return () => { alive = false }
  }, [nonce])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const toggle = (set: Set<string>, key: string, put: (s: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    put(next)
  }

  async function undo(historyIds: string[]) {
    try {
      const t = tidyUndoneToast(await undoAccountNameTidies(historyIds))
      toast.success(t.title, t.description ? { description: t.description } : undefined)
      onChanged()
      setNonce(n => n + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to undo the tidy'))
    }
  }

  async function tidy() {
    if (!data) return
    const items = data.tidies.filter(t => tidies.has(t.accountId)).map(t => ({ accountId: t.accountId, name: t.name }))
    if (items.length === 0) return
    setTidying(true)
    try {
      const r = await tidyAccountNames(items)
      const t = tidiedToast(r)
      const ids = r.tidied.map(x => x.historyId)
      toast.success(t.title, {
        ...(t.description ? { description: t.description } : {}),
        ...(ids.length > 0 ? { action: { label: 'Undo', onClick: () => { void undo(ids) } } } : {}),
      })
      onChanged()
      setNonce(n => n + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to tidy the names'))
    } finally {
      setTidying(false)
    }
  }

  const tidyCount = data ? data.tidies.filter(t => tidies.has(t.accountId)).length : 0
  const chosenMerges = data ? data.merges.filter(m => merges.has(mergeKey(m))) : []
  const nothing = data !== null && data.tidies.length === 0 && data.merges.length === 0

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer" role="dialog" aria-label="Fix all"
        style={{ width: 'min(600px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Fix all</div>
            <div className="text-12 text-ink-muted mt-1">Suggested clean-ups across every account. Nothing changes until you choose.</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-6">
          {!data && !error && <p className="text-13 text-ink-muted">Reading the clean-ups…</p>}
          {error && <p className="text-13 text-ink-secondary">{error}</p>}
          {nothing && <p className="text-13 text-ink-muted">No possible duplicates or lower-case names.</p>}

          {data && data.tidies.length > 0 && (
            <section aria-label="Tidy names" className="flex flex-col gap-2">
              <h3 className="text-13 font-semibold text-ink-primary">Tidy names <span className="text-ink-muted font-normal">{data.tidies.length}</span></h3>
              <p className="text-12 text-ink-muted">Each word starts with a capital letter. Tidied together, undone together.</p>
              <div className="flex flex-col">
                {data.tidies.map(t => (
                  <label key={t.accountId} className="flex items-center gap-3 py-2 border-b border-edge-row last:border-b-0 cursor-pointer">
                    <input type="checkbox" checked={tidies.has(t.accountId)} aria-label={`Tidy ${t.name} to ${t.tidied}`}
                      onChange={() => toggle(tidies, t.accountId, setTidies)} />
                    <span className="text-13 text-ink-secondary truncate">{t.name}</span>
                    <ArrowRight size={12} className="text-ink-faint flex-shrink-0" aria-hidden="true" />
                    <span className="text-13 font-semibold text-ink-primary truncate">{t.tidied}</span>
                  </label>
                ))}
              </div>
              {records && (
                <div>
                  <button type="button" className="k-btn disabled:opacity-60" disabled={tidying || tidyCount === 0} onClick={() => { void tidy() }}>
                    {tidying ? 'Tidying…' : `Tidy ${plural(tidyCount, 'name')}`}
                  </button>
                </div>
              )}
            </section>
          )}

          {data && data.merges.length > 0 && (
            <section aria-label="Possible duplicates" className="flex flex-col gap-2">
              <h3 className="text-13 font-semibold text-ink-primary">Possible duplicates <span className="text-ink-muted font-normal">{data.merges.length}</span></h3>
              {canMerge && (
                <p className="text-12 text-ink-muted">Each merge asks you to confirm it on its own, and each can be undone.</p>
              )}
              <div className="flex flex-col">
                {data.merges.map(m => {
                  const key = mergeKey(m)
                  const body = (
                    <span className="min-w-0 flex flex-col">
                      <span className="text-13 font-semibold text-ink-primary">{mergeTitle(m)}</span>
                      <span className="text-11 text-ink-muted">{mergeReason(m)}</span>
                      <span className="text-12 text-ink-secondary">{mergeLine(m)}</span>
                    </span>
                  )
                  return canMerge ? (
                    <label key={key} className="flex items-start gap-3 py-2 border-b border-edge-row last:border-b-0 cursor-pointer">
                      <input type="checkbox" className="mt-1" checked={merges.has(key)} aria-label={mergeTitle(m)}
                        onChange={() => toggle(merges, key, setMerges)} />
                      {body}
                    </label>
                  ) : (
                    <div key={key} className="flex items-start gap-3 py-2 border-b border-edge-row last:border-b-0">{body}</div>
                  )
                })}
              </div>
              {canMerge && (
                <div>
                  <button type="button" className="k-btn-secondary disabled:opacity-60" disabled={chosenMerges.length === 0}
                    onClick={() => onReviewMerges(chosenMerges)}>
                    Review {plural(chosenMerges.length, 'merge')}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
